import { useEffect, useRef, useState } from "react";

/**
 * Counts up from 0 to `target` over `duration` ms once the element scrolls into view.
 * Falls back to rendering raw text for non-numeric values.
 *
 *   <CountUp value="11" />          -> 0..11
 *   <CountUp value="9001" prefix="ISO " />  -> ISO 0..9001
 *   <CountUp value="CH" />          -> CH (no animation)
 */
export default function CountUp({ value, prefix = "", suffix = "", duration = 1600 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const numericMatch = String(value).match(/^(\d+)(\D*)$/);
    if (!numericMatch) {
      setDisplay(value);
      return;
    }
    const target = parseInt(numericMatch[1], 10);
    const trailing = numericMatch[2] || "";
    setDisplay(`0${trailing}`);

    let started = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (started) return;
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        started = true;
        io.disconnect();

        const t0 = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / duration);
          // easeOutCubic
          const eased = 1 - Math.pow(1 - t, 3);
          const cur = Math.round(target * eased);
          setDisplay(`${cur}${trailing}`);
          if (t < 1) requestAnimationFrame(tick);
          else setDisplay(`${target}${trailing}`);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
